import { HiLoSideGameCSS } from './HiLoSideGameCSS.styled'
import { cards } from './gameAssets/cards'
import { useHiLoGameLogic } from './useHiLoGameLogic'
import { useEffect, useState } from 'react'
import { isEmpty, isEqual } from 'lodash'
import { currencyFormatter } from 'utils'
import { LONG_DASH } from 'constants'
import { motion, AnimatePresence } from 'framer-motion'
import { HiLoRules } from './HiLoRules'
import { HiLoStats } from './HiLoStats'
import { useUser } from 'hooks/user/useUser'
import { HiLoSignInToPlay } from './HiLoSignInToPlay'
import { atomWithStorage } from 'jotai/utils'
import { HI_LO_CHIP_SELECTION } from 'constants'
import { useAtom } from 'jotai'
import { HiLoNoBalance } from './HiLoNoBalance'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDebouncedCallback } from 'utils/hooks/useDebounceCallback'

const CHIPS = [
  { id: 0, value: 2, unitValue: 2000000, formattedValue: '$2.00', class: 'two-dollars' },
  { id: 1, value: 5, unitValue: 5000000, formattedValue: '$5.00', class: 'five-dollars' },
  { id: 2, value: 20, unitValue: 20000000, formattedValue: '$20.00', class: 'twenty-dollars' },
]

const hiLoChipAtom = atomWithStorage(HI_LO_CHIP_SELECTION, CHIPS.at(1))

export const HiLoSideGame = () => {
  const { isConnected } = useUser('wallet')
  const { totalBalance } = useUser('balance')
  const { card, prevCard, payout, result, updates, dailyWinStreak, rank, hitGame } = useHiLoGameLogic()
  const [selectedChip, setSelectedChip] = useAtom(hiLoChipAtom)
  const [message, setMessage] = useState('Select a chip to start playing')

  const [showRules, setShowRules] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [needLoginFirst, setNeedLoginFirst] = useState('')
  const [needMoreBalance, setNeedMoreBalance] = useState('')
  const [otherAmount, setOtherAmount] = useState(selectedChip.formattedValue)

  const closeOverlays = () => {
    setShowStats(false)
    setShowRules(false)
    setNeedLoginFirst('')
    setNeedMoreBalance('')
  }

  const ref = useHotkeys('Escape', closeOverlays)

  useEffect(() => {
    if (isConnected) closeOverlays()
    return () => {
      closeOverlays()
    }
  }, [isConnected])

  useEffect(() => {
    if (selectedChip.formattedValue !== otherAmount) setOtherAmount(selectedChip.formattedValue)
  }, [selectedChip])

  const onHit = (prediction) => {
    if (!isConnected) setNeedLoginFirst(prediction)
    else if (Number(totalBalance) < selectedChip?.unitValue) setNeedMoreBalance(prediction)
    else hitGame(selectedChip.value, prediction)
  }

  const debouncedOnHit = useDebouncedCallback(onHit, 200)

  const currentCardFace = cards[`card_${card?.rank?.toLowerCase()}_${card?.suit?.toLowerCase()}`]
  const prevCardFace = cards[`card_${prevCard?.rank?.toLowerCase()}_${prevCard?.suit?.toLowerCase()}`]

  const selectChip = (chip) => {
    setSelectedChip(chip)
    setOtherAmount(chip.formattedValue)
    setMessage('Choose Hi, Lo, or Same')
  }
  const showRulesHandler = () => {
    setShowRules(true)
  }
  const showStatsHandler = () => {
    setShowStats(true)
  }
  const otherAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, '')
    if (!value) {
      setOtherAmount(`$${value}`)
      return
    }
    if (value > 20) value = 20
    if (value < 1) value = 1
    setOtherAmount(`$${value}`)
    if (value) {
      const definedChip = CHIPS.find((chip) => chip.value === Number(value))
      if (definedChip) setSelectedChip(definedChip)
      else {
        setSelectedChip({
          id: 3,
          value: Number(value),
          unitValue: Number(value) * 1000000,
          formattedValue: currencyFormatter(Number(value)),
        })
      }
    }

    if (!value) {
      setOtherAmount(`$${value}`)
      return
    }
    const regExpNumberWithTwoDecimals = /^\d+(?:\.\d{1,2})?$/
    if (!regExpNumberWithTwoDecimals.test(value)) return

    if (value > 20) value = 20
    if (value < 1) value = 1
    setOtherAmount(`$${value}`)
    if (value) {
      const definedChip = CHIPS.find((chip) => chip.value === Number(value))
      if (definedChip) setSelectedChip(definedChip)
      else {
        setSelectedChip({
          id: 3,
          value: Number(value),
          unitValue: Number(value) * 1000000,
          formattedValue: currencyFormatter(Number(value)),
        })
      }
    }
  }

  return (
    <HiLoSideGameCSS ref={ref} tabIndex={-1}>
      <div className='content'>
        {/* <!-- GAME --> */}
        <section className='hilo_wrapper'>
          <div className='row main'>
            <div className='streak_list_wrapper'></div>
            <div className='bid_buttons'>
              <div className='title'>Bid</div>
              <button
                className={`chip two-dollars ${isEqual(selectedChip, CHIPS.at(0)) ? 'selected' : 'disabled'}`}
                onClick={() => selectChip(CHIPS.at(0))}
              />
              <button
                className={`chip five-dollars ${isEqual(selectedChip, CHIPS.at(1)) ? 'selected' : 'disabled'}`}
                onClick={() => selectChip(CHIPS.at(1))}
              />
              <button
                className={`chip twenty-dollars ${isEqual(selectedChip, CHIPS.at(2)) ? 'selected' : 'disabled'}`}
                onClick={() => selectChip(CHIPS.at(2))}
              />
              <input type='tel' className='other_amount' value={otherAmount} onChange={otherAmountChange} />
            </div>

            <div className='cards_wrap'>
              <AnimatePresence>
                {currentCardFace && (
                  <motion.div
                    key={`front_card-${updates}`}
                    className='single_card front'
                    initial={{ top: '80%', left: '80%', opacity: 0 }}
                    animate={{ top: '50%', left: '50%', opacity: 1, transition: { duration: 0.1 } }}
                    exit={{ top: 'calc(50% - 40px)', left: 'calc(50% - 38px)', height: '40%', boxShadow: 'none' }}
                  >
                    <img alt='current card' src={currentCardFace} />
                  </motion.div>
                )}
                {prevCardFace && (
                  <motion.div
                    key={`back_card-${updates}`}
                    className='single_card back'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.3, duration: 0.1 } }}
                  >
                    <img alt='previous card' src={prevCardFace} />
                  </motion.div>
                )}
              </AnimatePresence>
              <div
                className='display_stats'
                onClick={showStatsHandler}
                // layoutId='statsDD'
                // initial={{ opacity: 0, scale: 0 }}
                // animate={{ opacity: 1, scale: 1 }}
                // exit={{ opacity: 0, scale: 2 }}
              >
                <div className='streak'>
                  <span>Streak</span>
                  <span>{dailyWinStreak?.current ?? LONG_DASH}</span>
                </div>
                <div className='rank'>
                  <span>Rank</span>
                  <span>{rank ?? LONG_DASH}</span>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {!isEmpty(selectedChip) && (
                <motion.div className='direction_buttons'>
                  <motion.fieldset
                    className='hi'
                    layoutId='high'
                    initial={{ y: -40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    <div className='payout_value'>{payout?.high ? `${payout?.high}x` : LONG_DASH}</div>
                    <button disabled={!payout?.high} onClick={() => debouncedOnHit('high')}></button>
                  </motion.fieldset>
                  <motion.fieldset
                    className='same'
                    layoutId='equal'
                    initial={{ x: 40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                  >
                    <button onClick={() => debouncedOnHit('equal')}>
                      <div className='payout_value'>{payout?.equal ? `${payout?.equal}x` : LONG_DASH}</div>
                    </button>
                  </motion.fieldset>
                  <motion.fieldset
                    className='lo'
                    layoutId='low'
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    <button disabled={!payout?.low} onClick={() => debouncedOnHit('low')}></button>
                    <div className='payout_value'>{payout?.low ? `${payout?.low}x` : LONG_DASH}</div>
                  </motion.fieldset>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className='row status'>
            <AnimatePresence>
              <motion.button
                className='stats_button'
                onClick={showRulesHandler}
                layoutId='rules'
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                Rules
              </motion.button>
            </AnimatePresence>

            <div className='status_message'>
              <motion.div
                key={`back_card-${updates}`}
                initial={result?.status === 'Won' ? { scale: 2, opacity: 0.5 } : false}
                animate={{ scale: 1, opacity: 1 }}
              >
                {prevCardFace ? `You ${result?.status}${result?.status === 'Won' ? '!' : ''}` : message}
              </motion.div>
            </div>
            <div className={`payout_amount ${result?.status?.toLowerCase() || 'neutral'}`}>
              {prevCardFace ? `${currencyFormatter(result?.payoutAmountReceived)}` : LONG_DASH}
            </div>
          </div>
        </section>
        {showRules && <HiLoRules layoutId='rules' onClose={closeOverlays} />}
        {showStats && <HiLoStats layoutId='statsDD' onClose={closeOverlays} />}
        {needLoginFirst && <HiLoSignInToPlay layoutId={needLoginFirst} />}
        {needMoreBalance && <HiLoNoBalance layoutId={needMoreBalance} />}
      </div>
    </HiLoSideGameCSS>
  )
}
